import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, User, Building2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton } from "@/pages/Auth";

const F = ({ label, children }) => <div><label className="label">{label}</label>{children}</div>;

export const BusinessFields = ({ biz, setBiz, t }) => {
  const b = (k) => ({ value: biz[k] || "", onChange: (e) => setBiz({ ...biz, [k]: e.target.value }), className: "field", "data-testid": `biz-${k}-input` });
  return (
    <div className="space-y-3 rounded-2xl bg-[#F8F9FC] p-4" data-testid="business-fields">
      <p className="font-display text-sm font-bold text-[#3B1272]">{t("auth.businessData")}</p>
      <F label={t("auth.companyName")}><input required {...b("company_name")} /></F>
      <div className="grid grid-cols-2 gap-3">
        <F label={t("auth.vat")}><input required pattern="\d{11}" maxLength={11} {...b("vat_number")} /></F>
        <F label={t("auth.taxCode")}><input maxLength={16} {...b("tax_code")} /></F>
        <F label={t("auth.sdi")}><input maxLength={7} {...b("sdi_code")} /></F>
        <F label={t("auth.pec")}><input type="email" {...b("pec")} /></F>
      </div>
      <F label={t("auth.address")}><input required {...b("address")} /></F>
      <div className="grid grid-cols-3 gap-3">
        <F label={t("auth.city")}><input required {...b("city")} /></F>
        <F label={t("auth.zip")}><input required pattern="\d{5}" maxLength={5} {...b("zip_code")} /></F>
        <F label={t("auth.province")}><input required maxLength={2} {...b("province")} /></F>
      </div>
    </div>
  );
};

export const cleanBiz = (biz) => Object.fromEntries(Object.entries(biz).map(([k, v]) => [k, v === "" ? null : k === "province" ? v.toUpperCase() : v]));

export default function RegisterPage() {
  const { t, lang } = useI18n();
  const { signedIn } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("private");
  const [f, setF] = useState({ email: "", password: "", first_name: "", last_name: "", phone: "", city: "", accept_terms: false, accept_privacy: false, marketing_consent: false });
  const [wantPro, setWantPro] = useState(false);
  const [biz, setBiz] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const inp = (k, extra = {}) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), className: "field", "data-testid": `register-${k}-input`, ...extra });
  const chk = (k) => ({ checked: f[k], onChange: (e) => setF({ ...f, [k]: e.target.checked }), type: "checkbox", className: "mt-0.5 h-4 w-4 accent-[#3B1272]", "data-testid": `register-${k}-checkbox` });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const body = { ...f, phone: f.phone || null, city: f.city || null, account_type: type, language: lang, business: type === "business" ? cleanBiz(biz) : null };
      const { data } = await api.post("/auth/register", body);
      signedIn(data);
      nav(type === "business" && wantPro ? "/account?tab=payments" : "/account", { replace: true });
    } catch (e2) {
      setErr(errMsg(e2));
    }
    setBusy(false);
  };

  return (
    <AuthShell title={t("auth.registerTitle")} sub={t("auth.registerSub")} testid="register-page">
      <GoogleButton />
      <div className="my-5 flex items-center gap-3 text-xs text-[#94A3B8]"><span className="h-px flex-1 bg-[#EEF0F6]" />{t("auth.or")}<span className="h-px flex-1 bg-[#EEF0F6]" /></div>
      <div className="mb-5 grid grid-cols-2 gap-2 rounded-full bg-[#F1F4F9] p-1">
        {[["private", User], ["business", Building2]].map(([k, Icon]) => (
          <button key={k} type="button" onClick={() => setType(k)} data-testid={`register-type-${k}`} className={`flex items-center justify-center gap-1.5 rounded-full py-2 text-xs font-bold transition-colors ${type === k ? "bg-white text-[#3B1272] shadow-sm" : "text-[#64748B]"}`}><Icon className="h-3.5 w-3.5" />{t(`auth.${k}`)}</button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-3.5">
        <div className="grid grid-cols-2 gap-3"><F label={t("auth.firstName")}><input required {...inp("first_name")} /></F><F label={t("auth.lastName")}><input required {...inp("last_name")} /></F></div>
        <F label={t("auth.email")}><input type="email" required {...inp("email")} /></F>
        <F label={t("auth.password")}><input type="password" required minLength={8} {...inp("password")} /><p className="mt-1 text-xs text-[#94A3B8]">{t("auth.pwHint")}</p></F>
        <div className="grid grid-cols-2 gap-3"><F label={t("auth.phone")}><input {...inp("phone")} /></F><F label={t("auth.city")}><input {...inp("city")} /></F></div>
        {type === "business" && <BusinessFields biz={biz} setBiz={setBiz} t={t} />}
        <div className="space-y-2 pt-1 text-sm">
          <label className="flex gap-2.5"><input required {...chk("accept_terms")} /><span>{t("auth.terms")} (<Link to="/informative" className="text-[#3B1272] underline">↗</Link>)</span></label>
          <label className="flex gap-2.5"><input required {...chk("accept_privacy")} /><span>{t("auth.privacy")} (<Link to="/informative" className="text-[#3B1272] underline">↗</Link>)</span></label>
          {type === "business" && <label className="flex gap-2.5"><input type="checkbox" checked={wantPro} onChange={(e) => setWantPro(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#3B1272]" data-testid="register-want-pro-checkbox" /><span>{t("p4.pay.wantPro")}</span></label>}
          <label className="flex gap-2.5"><input {...chk("marketing_consent")} /><span className="text-[#64748B]">{t("auth.marketing")}</span></label>
        </div>
        {err && <p className="text-sm text-red-600" data-testid="register-error">{err}</p>}
        <button disabled={busy} className="btn-coral w-full !py-3" data-testid="register-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("auth.register")}</button>
      </form>
      <p className="mt-5 text-center text-sm">{t("auth.haveAccount")} <Link to="/accedi" className="font-semibold text-[#3B1272]" data-testid="goto-login-link">{t("auth.login")}</Link></p>
    </AuthShell>
  );
}
